import { FormattedMessage } from 'react-intl';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import Legend from '@rio-cloud/rio-uikit/Legend';
import PieChart from '@rio-cloud/rio-uikit/PieChart';

import { ColorMappings } from './ColorMappings';

type VehiclesStatusWidgetComponentProps = {
    responsiveGridConfig?: string;
    isLoading: boolean;
    totalCriticalStatus: number;
    totalWarningStatus: number;
    totalOkStatus: number;
    totalUnknownStatus: number;
};

type DataElement = {
    index: number;
    name: string;
    value?: number;
    color: string;
};

type CustomPieChartComponentProps = {
    data: DataElement[];
};

type LegendPayload = {
    color: string;
    payload: DataElement;
    value: string;
};

const renderLegendItem = (props: unknown) => {
    const { payload } = props as { payload: LegendPayload[] };
    const payloadSorted = payload.sort((a, b) => a.payload.index - b.payload.index);
    return (
        <ul>
            {payloadSorted.map((legend: LegendPayload) => {
                const { color, payload: innerPayload, value } = legend;
                const total = innerPayload?.value;

                if (total === 0) {
                    // hide legend if value is 0
                    return null;
                }

                // Note: &#9632; is the HTML code for a filled square
                return (
                    <li key={`item-${color}`} style={{ listStyle: 'none' }}>
                        <div data-testid="vehicle-status-widget-legend-item" className="text-color-darkest">
                            <span style={{ color }}>&#9632; </span>
                            <span className="text-bold">{total}</span> <FormattedMessage id={value} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

const CustomPieChartComponent = ({ data }: CustomPieChartComponentProps) => (
    <PieChart
        data={data}
        dataKey="value"
        nameKey="name"
        labels={false}
        tooltip={false}
        // items will already be injected from data on payload prop inside legend
        legend={<Legend items={[]} content={renderLegendItem} />}
        innerRadius={37}
        outerRadius={45}
        height={90}
        width={240}
    />
);

const getDataStructure = (
    totalCriticalStatus?: number,
    totalWarningStatus?: number,
    totalOkStatus?: number,
    totalUnknownStatus?: number,
): DataElement[] => [
    {
        index: 0,
        name: 'intl-msg:vehicle_status.critical',
        value: totalCriticalStatus,
        color: ColorMappings.CRITICAL.chartColorName,
    },
    {
        index: 1,
        name: 'intl-msg:vehicle_status.warning',
        value: totalWarningStatus,
        color: ColorMappings.WARNING.chartColorName,
    },
    {
        index: 2,
        name: 'intl-msg:vehicle_status.ok',
        value: totalOkStatus,
        color: ColorMappings.OK.chartColorName,
    },
    {
        index: 3,
        name: 'intl-msg:vehicle_status.unknown',
        value: totalUnknownStatus,
        color: ColorMappings.UNKNOWN.chartColorName,
    },
];

const SkeletonScreen = () => (
    <div
        data-testid="vehicles-status-widget-skeleton-screen"
        className="display-flex flex-row justify-content-center align-items-center margin-left-20"
    >
        <ContentLoader height={100} width={120} />
        <div className="margin-left-25" />
        <ContentLoader height={50} width={100} />
    </div>
);

const VehiclesStatusWidgetComponent = ({
    responsiveGridConfig,
    isLoading,
    totalCriticalStatus,
    totalWarningStatus,
    totalOkStatus,
    totalUnknownStatus,
}: VehiclesStatusWidgetComponentProps) => {
    const pieChartData = getDataStructure(totalCriticalStatus, totalWarningStatus, totalOkStatus, totalUnknownStatus);

    return (
        <div className={`panel panel-default panel-body display-flex flex-column ${responsiveGridConfig}`}>
            <h4 className="text-center text-bold margin-top-0 margin-bottom-20">
                <FormattedMessage id="intl-msg:vehicle_status.widgets.vehicles_status_widget.title" />
            </h4>
            <div className="display-flex justify-content-center" style={{ height: 90 }}>
                {isLoading ? <SkeletonScreen /> : <CustomPieChartComponent data={pieChartData} />}
            </div>
        </div>
    );
};

export default VehiclesStatusWidgetComponent;
